import React, { memo, Suspense, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import { LoadingSpinner } from 'components/layouts';

import { Routes } from '../../../weborder/Routes';
import { Button, IconButton } from '../../atoms';
import { Profile as ProfileIcon } from '../../icons';
import { AddCreditCardDialog } from '../AddCreditCardDialog/AddCreditCardDialog';
import { PaymentMethodsDialog } from '../PaymentMethodsDialog';
import { useProfileActions } from './hooks/useProfileActions';
import { DispatchProps, StateProps } from './index';
import { ProfileDrawer } from './partials/ProfileDrawer';

const AboutMyDataDialog = React.lazy(() => import('../AboutMyDataDialog'));

export interface OuterProps {
  params: Routes['params'];
}

const useStyles = makeStyles((theme: Theme) => ({
  iconButton: {
    backgroundColor: 'transparent',
  },
  loginButton: {
    border: '1px solid transparent',
    '&:hover': {
      borderBottom: `1px dashed ${theme.palette.primary.main}`,
    },
  },
}));

type Props = StateProps & DispatchProps & OuterProps;
function ProfileMenuComponent(props: Props): JSX.Element {
  const {
    actions,
    collectionSummaries,
    configData,
    contactFormType,
    customer,
    hasDisableNonEssentialCookies,
    hasGdprCookieFeatureFlag,
    intercom,
    languageCode,
    params,
    payments,
    savedCardCount,
    showRestaurantList,
    translate,
  } = props;
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [showPaymentMethodsDialog, setShowPaymentMethodsDialog] =
    useState(false);
  const [showAddCardDialog, setShowAddCardDialog] = useState(false);
  const { customerProfileActions, redirectToLogin, showAboutMyData } =
    useProfileActions(
      actions,
      configData,
      collectionSummaries,
      intercom,
      showRestaurantList,
      params,
    );
  const classes = useStyles();

  const handleOpenAddCardDialog = (): void => {
    setShowPaymentMethodsDialog(false);
    setShowAddCardDialog(true);
  };

  if (!customer?.status || customer.status === 'pending') {
    return <LoadingSpinner variant="inline" />;
  }

  if (customer.status === 'success' && !customer.data.IsGuest) {
    return (
      <>
        <IconButton
          aria-label={translate('Profile_Menu') as string}
          className={classes.iconButton}
          onClick={(): void => setShowDrawer(true)}
        >
          <ProfileIcon />
        </IconButton>
        <ProfileDrawer
          customerProfileActions={customerProfileActions}
          configData={configData}
          contactFormType={contactFormType}
          customerData={customer.data}
          hasDisableNonEssentialCookies={hasDisableNonEssentialCookies}
          hasGdprCookieFeatureFlag={hasGdprCookieFeatureFlag}
          languageCode={languageCode}
          onCloseDrawer={(): void => setShowDrawer(false)}
          onShowPaymentsDialog={(): void => setShowPaymentMethodsDialog(true)}
          showDrawer={showDrawer}
          translate={translate}
        />
        <Suspense fallback={<LoadingSpinner />}>
          <PaymentMethodsDialog
            addCard={handleOpenAddCardDialog}
            deletePaymentMethod={customerProfileActions.onDeletePaymentMethod}
            payments={payments}
            setShowDialog={setShowPaymentMethodsDialog}
            showDialog={showPaymentMethodsDialog}
            translate={translate}
          />
        </Suspense>
        <Suspense fallback={<LoadingSpinner />}>
          <AddCreditCardDialog
            addCardPayment={customerProfileActions.onAddPaymentCard}
            cancelAddCardBridge={customerProfileActions.onCancelAddCardBridge}
            clearBasketStatus={customerProfileActions.onClearBasketStatus}
            configData={configData}
            currentLanguage={languageCode}
            isFromCheckoutPage={false}
            savedCardCount={savedCardCount}
            setShowDialog={setShowAddCardDialog}
            showDialog={showAddCardDialog}
            translate={translate}
          />
        </Suspense>
        {showAboutMyData && (
          <Suspense fallback={<LoadingSpinner />}>
            <AboutMyDataDialog
              handleOnClick={customerProfileActions.onAboutMyData}
              showDialog={showAboutMyData}
              translate={translate}
            />
          </Suspense>
        )}
      </>
    );
  }
  return (
    <Button
      className={classes.loginButton}
      data-fd="login-button"
      onClick={redirectToLogin}
      variant="text"
    >
      {translate('Login')}
    </Button>
  );
}

const ProfileMenu = memo(ProfileMenuComponent);
ProfileMenu.displayName = 'ProfileMenu';

export { ProfileMenu };
